import { describeQuantity } from "../../../lib/quantity";
import { formatPesos } from "../../../lib/money";
import { Table } from "../../ui";
import { PosChip, ReferenceNumber } from "../partLabels";
import type { PlateRow } from "./plateRows";

export interface PlatePartsTableProps {
  rows: PlateRow[];
  selectedKey: string | null;
  selectedPosition: number | null;
  onPick: (row: PlateRow) => void;
  /** An unnumbered list has no Pos. column. */
  numbered: boolean;
  className?: string;
}

/** The desktop parts list beside the plate. One row per listing; a shared position shows its chip once. */
export function PlatePartsTable({ rows, selectedKey, selectedPosition, onPick, numbered, className }: PlatePartsTableProps) {
  return (
    <Table
      rows={rows}
      rowKey={(row) => row.key}
      selectedRow={selectedKey ?? undefined}
      onRowClick={onPick}
      className={className}
      columns={[
        ...(numbered
          ? [
              {
                key: "pos",
                label: "Pos.",
                width: 60,
                align: "center" as const,
                render: (row: PlateRow) =>
                  row.first ? (
                    <PosChip label={row.position.position_number} active={row.position.id === selectedPosition} />
                  ) : (
                    <span title={`Another part at position ${row.position.position_number}`} className="type-sku text-ink-300">
                      ↳
                    </span>
                  ),
              },
            ]
          : []),
        { key: "number", label: "Part number", width: 150, render: (row) => <ReferenceNumber listing={row.listing} /> },
        {
          key: "name",
          label: "Description",
          render: (row) => (
            <span className="block min-w-0">
              <span className="block text-text-strong">{row.listing.name}</span>
              {/* Under the name, not a column: most rows have none, but it tells standard bolts apart. */}
              {row.listing.dimension && <span className="block type-sku [font-size:var(--text-sm)] text-ink-600">{row.listing.dimension}</span>}
              {row.first && row.groupSize > 1 && (
                <span className="mt-0.5 block type-small text-gold-700">{row.groupSize} versions at this position. Pick yours by the description.</span>
              )}
              {row.first && row.position.position_note && <span className="mt-0.5 block type-small text-text-muted">{row.position.position_note}</span>}
              {row.listing.replaced_by && <span className="mt-0.5 block type-small text-warning-700">Replaced by {row.listing.replaced_by.sku}</span>}
            </span>
          ),
        },
        {
          key: "qty",
          label: "Qty",
          width: 64,
          align: "center",
          mono: true,
          render: (row) => (row.listing.optional ? <span title="As required: fitted to measurement">As req.</span> : describeQuantity(row.listing)),
        },
        { key: "price", label: "Price", width: 100, align: "right", mono: true, render: (row) => formatPesos(row.listing.price_cents) },
        {
          key: "stock",
          label: "Stock",
          width: 56,
          align: "center",
          render: (row) => (
            <span
              role="img"
              aria-label={row.listing.in_stock ? "In stock" : "Out of stock"}
              title={row.listing.in_stock ? "In stock" : "Out of stock"}
              className={`mx-auto block size-[9px] rounded-full ${row.listing.in_stock ? "bg-green-600" : "bg-danger-500"}`}
            />
          ),
        },
      ]}
    />
  );
}
